import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, SignOut, List, X } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_uds-plans-store/artifacts/5x9ta2nh_UDS%203D%20logo%20black.webp";

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { getItemCount } = useCart();
  const itemCount = getItemCount();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border" data-testid="main-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="UDS Plans" className="h-14 w-auto object-contain" />
            <div className="border-l border-border pl-3">
              <h1 className="text-lg font-bold text-primary leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                UDS PLANS
              </h1>
              <p className="text-[10px] text-text_muted uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>UmPhakathi Design Studio</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/plans" className="text-sm font-medium text-text_main hover:text-accent transition-colors" data-testid="nav-plans">
              Browse Plans
            </Link>
            <a
              href="https://quote.umphakathids.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-text_main hover:text-accent transition-colors"
              data-testid="nav-custom-design"
            >
              Custom Design
            </a>
            {isAuthenticated && (
              <>
                <Link to="/orders" className="text-sm font-medium text-text_main hover:text-accent transition-colors" data-testid="nav-orders">
                  My Orders
                </Link>
                <Link to="/favourites" className="text-sm font-medium text-text_main hover:text-accent transition-colors" data-testid="nav-favourites">
                  Favourites
                </Link>
              </>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-sm font-medium text-text_main hover:text-accent transition-colors" data-testid="nav-admin">
                Admin
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link to="/cart" className="relative p-2 hover:bg-gray-100 rounded transition-colors" data-testid="cart-btn">
                  <ShoppingCart size={24} className="text-text_main" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-mono">
                      {itemCount}
                    </span>
                  )}
                </Link>
                <div className="flex items-center space-x-2">
                  <Link
                    to="/dashboard"
                    className="hover:opacity-80 transition-opacity"
                    data-testid="profile-avatar-link"
                    title="My Dashboard"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                        {user?.name?.trim()?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="p-2 hover:bg-gray-100 rounded transition-colors"
                    data-testid="logout-btn"
                    title="Logout"
                  >
                    <SignOut size={20} className="text-text_main" />
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 bg-primary text-white rounded hover:bg-opacity-90 transition-colors text-sm font-medium"
                data-testid="login-btn"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden relative" ref={mobileMenuRef}>
            <button
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-text_main" />
              ) : (
                <List size={24} className="text-text_main" />
              )}
            </button>

            {mobileMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 bg-white border border-border rounded-lg shadow-lg py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                data-testid="mobile-menu-dropdown"
              >
                <Link
                  to="/plans"
                  onClick={closeMobileMenu}
                  className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                  data-testid="mobile-nav-plans"
                >
                  Browse Plans
                </Link>
                <a
                  href="https://quote.umphakathids.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                  data-testid="mobile-nav-custom-design"
                >
                  Custom Design
                </a>
                {isAuthenticated && (
                  <>
                    <Link
                      to="/orders"
                      onClick={closeMobileMenu}
                      className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                      data-testid="mobile-nav-orders"
                    >
                      My Orders
                    </Link>
                    <Link
                      to="/favourites"
                      onClick={closeMobileMenu}
                      className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                      data-testid="mobile-nav-favourites"
                    >
                      Favourites
                    </Link>
                    <Link
                      to="/cart"
                      onClick={closeMobileMenu}
                      className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                      data-testid="mobile-nav-cart"
                    >
                      <span>Cart</span>
                      {itemCount > 0 && (
                        <span className="bg-accent text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-mono">
                          {itemCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/dashboard"
                      onClick={closeMobileMenu}
                      className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                      data-testid="mobile-nav-dashboard"
                    >
                      My Dashboard
                    </Link>
                  </>
                )}
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={closeMobileMenu}
                    className="block px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                    data-testid="mobile-nav-admin"
                  >
                    Admin
                  </Link>
                )}

                <div className="border-t border-border my-2"></div>

                {isAuthenticated ? (
                  <button
                    onClick={() => { closeMobileMenu(); logout(); }}
                    className="flex items-center space-x-2 w-full text-left px-4 py-2.5 text-sm font-medium text-text_main hover:bg-gray-100 transition-colors"
                    data-testid="mobile-logout-btn"
                  >
                    <SignOut size={18} />
                    <span>Logout</span>
                  </button>
                ) : (
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block px-4 py-2.5 text-sm font-medium text-accent hover:bg-gray-100 transition-colors"
                    data-testid="mobile-login-btn"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
